/**
 * Humanized rendering helpers. Money is stored as integer microUSDC (1 USDC =
 * 1_000_000) in Firestore — NEVER as a float. We only ever format for display;
 * we never do arithmetic that could lose precision.
 */

const USDC_FORMATTER = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** microUSDC in one cent. USDC has 6 decimals, so amounts are not always whole cents. */
const MICRO_USDC_PER_CENT = 10_000;

/**
 * Format integer microUSDC as a "$1,234.56" string.
 *
 * Sub-cent remainders are **floored, never rounded**. Intl's default rounding is
 * half-expand, which renders 999_999 microUSDC ($0.999999) as "$1.00" — a figure higher
 * than the amount actually is. On a card's spending limit that would advertise headroom
 * the card does not have, and the same objection applies to every balance in this console:
 * an operator figure may under-state by less than a cent, never over-state. Flooring rather
 * than truncating toward zero keeps that true for negative amounts too (an overdrawn
 * −$0.999999 shows as "−$1.00", not "−$0.00").
 *
 * Nothing is lost: `<Money>` puts the exact integer in a tooltip.
 */
export function formatMicroUsdc(micro: number | null | undefined): string {
  if (micro === null || micro === undefined) return "—";
  // Divide as a final display step only. microUSDC fits in a JS safe integer for
  // any realistic balance (2^53 ≈ $9e9 in USDC), so this is exact for our range:
  // IEEE division is correctly rounded, and an exact multiple of 10_000 yields its
  // integer quotient exactly, so `floor` never shaves a cent off a whole-cent amount.
  return USDC_FORMATTER.format(Math.floor(micro / MICRO_USDC_PER_CENT) / 100);
}

/** Format a local-currency minor-unit amount (e.g. cents) with its ISO currency. */
export function formatLocalMinor(
  minor: number | null | undefined,
  currency: string | null | undefined,
): string {
  if (minor === null || minor === undefined) return "—";
  const cur = (currency ?? "USD").toUpperCase();
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: cur,
      // Most currencies are 2-minor-digit; Intl picks the right scale per currency.
    }).format(minor / 100);
  } catch {
    // Unknown/invalid currency code — fall back to a plain number + code.
    return `${(minor / 100).toFixed(2)} ${cur}`;
  }
}

/** Basis points → percentage string, e.g. 1234 → "12.34%". */
export function formatBps(bps: number | null | undefined): string {
  if (bps === null || bps === undefined) return "—";
  return `${(bps / 100).toFixed(2)}%`;
}

/** Shorten a 0x address / tx hash to 0x1234…abcd. */
export function shortHash(value: string | null | undefined, lead = 6, tail = 4): string {
  if (!value) return "—";
  if (value.length <= lead + tail + 1) return value;
  return `${value.slice(0, lead)}…${value.slice(-tail)}`;
}

/** Turn a camelCase / kebab-case enum value into a human Title Case label. */
export function humanize(value: string | null | undefined): string {
  if (!value) return "—";
  return value
    .replace(/[-_]/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** A wallet's two chains: HyperEVM mainnet (999) / testnet (998). */
export function chainName(chainId: number | null | undefined): string {
  switch (chainId) {
    case 999:
      return "HyperEVM mainnet (999)";
    case 998:
      return "HyperEVM testnet (998)";
    case null:
    case undefined:
      return "—";
    default:
      return `chain ${chainId}`;
  }
}

/** Build a full name from first/last, falling back to a handle or em-dash. */
export function fullName(
  first: string | null | undefined,
  last: string | null | undefined,
  handle?: string | null,
): string {
  const name = [first, last].filter(Boolean).join(" ").trim();
  if (name) return name;
  if (handle) return `@${handle}`;
  return "—";
}
